import { useContext, useEffect, useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { parse, stringify } from 'query-string'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import styled from 'styled-components'

import { palette, fonts } from '../../styles/variables'
import { PrimaryButton, CloseButton } from '../buttons/Buttons'
import { pushWithLanguageRoutes, translate } from '../../utils/translation'
import { Text } from '../topography'
import Icon from '../icon/Icon'
import leftBanner from '../../styles/assets/internalExitModal/leftBanner.png'
import darellIcon from '../../styles/assets/internalExitModal/darell.png'
import chartIcon from '../../styles/assets/atp/chart.svg'
import starIcon from '../../styles/assets/atp/star.svg'
import greyQuestionIcon from '../../styles/assets/atp/question_grey.svg'
import satisfiedUsersImage from '../../styles/assets/internalExitModal/satisfied_users.png'
import greySearchIcon from '../../styles/assets/atp/search_grey.svg'
import doubleQuotesIcon from '../../styles/assets/atp/double_quotes.svg'
import { isPaidUser, getUserTier } from '../../utils/freeVersionBlock'
import { AppContext } from '../../containers/Layout'
import { lifetimeOffer } from '../../utils/api'
import { getLoggedUser } from '../../utils/userAuth'
import { fetchUser } from '../../state/actions/global'
import { Mixpanel, MIXPANEL_EVENTS, MIXPANEL_PROPS } from '../../utils/mixpanel'
import history from '../../history'
import { LoadingWheel } from '../loading/Loading'
import { growthbookFeatures } from '../../utils/growthbook'

import Modal, { ModalContent } from './Modal'

const desktopStyle = {
  width: 'fit-content',
  borderRadius: 0,
  margin: 'auto',
  overflow: 'auto',
  maxHeight: 'calc(100vh - 100px)',
}

const overlayStyle = {
  maxHeight: '100vh',
  alignItems: 'initial',
  justifyContent: 'initial',
  padding: '50px',
  zIndex: 20001,
}

const ModalContentWrapper = styled(ModalContent)({
  width: '1308px',
  maxWidth: '1308px',
  display: 'flex',
  flexDirection: 'row',
  position: 'relative',
  padding: '0px',
  margin: '0px',
})

const ImageWrapper = styled.div({
  width: 330,
  backgroundImage: `url(${leftBanner})`,
  backgroundSize: '100% 100%',
  backgroundRepeat: 'no-repeat',
})

const RightWrapper = styled.div({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  margin: '48px',
})

const OffersWrapper = styled.div({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  margin: '34px 0',
  gap: '48px',
})

const OfferWrapper = styled.div({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  alignItems: 'center',
})

const ReviewWrapper = styled.div({
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  background: palette.lightGray10,
})

const AnnReviewWrapper = styled.div({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  margin: '32px',
  zIndex: 1,
})

const AnnWrapper = styled.div({
  display: 'flex',
  flexDirection: 'row',
  margin: '20px 0 0',
  alignItems: 'center',
})

const AnnInfoWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  margin: '0 0 0 12px',
})

const ReviewUsersWrapper = styled.div({
  display: 'flex',
  height: '100%',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(90deg, ${palette.lightGray20} 100%, ${palette.lightGray30} 100%)`,
})

const UsersWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0 24px',
})

const RightBottomWrapper = styled.div({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  margin: '22px 0 0',
})

const ComparedMonthlySubscriptionWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
})

const LimitedOfferWrapper = styled.div({
  position: 'absolute',
  left: 24,
  top: 13,
})

const OfferButton = styled(PrimaryButton)({
  width: '100%',
  height: '56px',
  margin: '6px 0 0px',
  fontSize: '16px',
  fontWeight: '500',
  lineHeight: '16px',
  letterSpacing: '1.12px',
  color: palette.white,
  background: palette.blue,
  border: palette.blue,
  '&:hover': {
    backgroundColor: palette.deepBlue,
  },
})

const COUNT_LIMIT = 3
const EXIT_MODAL_KEY_COUNT = 'eimss_count'
const EXIT_MODAL_KEY = 'eimss'
const HIDE_MODAL_PAGES = ['/pricing', '/login', '/register', '/checkout']
const HIDE_MODAL_ELEMENTS = [
  '[data-testid="upgrade-button"]',
  '.recurly-element-inline',
]
const USER_COUPON = 'lifetime_5_offer'

export default ({ isOpen, onClose }) => {
  const dispatch = useDispatch()
  const { contextValue, setAppContext } = useContext(AppContext)
  const [open, setOpen] = useState(false)
  const [getLifetimeOffer, seGetLifetimeOffer] = useState(false)
  const [getLifetimeOfferError, seGetLifetimeOfferError] = useState(false)

  const updateUserInfo = useCallback(
    (callback) =>
      dispatch(
        fetchUser(() => {
          if (callback) callback()
        }),
      ),
    [dispatch],
  )

  const localCount = JSON.parse(
    localStorage.getItem(EXIT_MODAL_KEY_COUNT) || '{}',
  )

  const sessionValue = JSON.parse(
    sessionStorage.getItem(EXIT_MODAL_KEY) || 'false',
  )

  const userCount = localCount?.[getUserTier()] || 0

  const exitIntentModalFeatureIsOn = useFeatureIsOn(
    growthbookFeatures.EXIT_INTENT_MODAL,
  )

  const showModal =
    !getLoggedUser()?.flags?.lifetime5_offer_accepted &&
    exitIntentModalFeatureIsOn &&
    !isPaidUser() &&
    (userCount < COUNT_LIMIT || getLifetimeOfferError || getLifetimeOffer) &&
    (((!sessionValue || getLifetimeOfferError || getLifetimeOffer) && open) ||
      isOpen)

  const handleMouseLeave = useCallback(() => {
    const { pathname } = window.location
    const { appCoupon } = parse(window.location.search)
    const user = getLoggedUser()
    let elemDetected = false
    for (let i = 0; i < HIDE_MODAL_ELEMENTS?.length; i += 1) {
      if (document.querySelector(HIDE_MODAL_ELEMENTS[i])) {
        elemDetected = true
        break
      }
    }
    if (elemDetected || appCoupon || user?.flags?.lifetime5_offer_accepted)
      return
    if (!HIDE_MODAL_PAGES.some((link) => pathname.includes(link))) {
      setOpen(true)
    }
  }, [])

  useEffect(() => {
    if (showModal && !isOpen) {
      Mixpanel.track(MIXPANEL_EVENTS.EXIT_INTENT_MODAL_VIEW, {
        [MIXPANEL_PROPS.PROMOTION.name]:
          MIXPANEL_PROPS.PROMOTION.values.LIFETIME_5,
      })
    }
  }, [isOpen, showModal])

  useEffect(() => {
    const body = document.querySelector('body')
    if (body) body.addEventListener('mouseleave', handleMouseLeave)
    return () => {
      if (body) body.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [handleMouseLeave])

  const handleClose = useCallback(() => {
    seGetLifetimeOfferError(false)
    sessionStorage.setItem(EXIT_MODAL_KEY, 'true')
    if (isOpen && onClose) onClose()
    setOpen(false)
  }, [onClose, isOpen])

  const hadleCount = () => {
    Mixpanel.track(MIXPANEL_EVENTS.INTENT_MODAL_EXIT, {
      [MIXPANEL_PROPS.PROMOTION.name]:
        MIXPANEL_PROPS.PROMOTION.values.LIFETIME_5,
      [MIXPANEL_PROPS.DISMISSED_MODAL.name]:
        MIXPANEL_PROPS.DISMISSED_MODAL.values.MODAL_CLOSE,
    })
    const userTier = getUserTier()
    if (!isOpen) {
      if (localCount[userTier]) localCount[userTier] += 1
      else localCount[userTier] = 1
    }
    localStorage.setItem(EXIT_MODAL_KEY_COUNT, JSON.stringify(localCount))
    handleClose()
  }

  const setLifetimeOffer = useCallback(
    (userStatus) => {
      seGetLifetimeOfferError(false)
      seGetLifetimeOffer(true)
      lifetimeOffer()
        .then(() => {
          const parsed = parse(window.location.search)
          delete parsed.appCoupon
          const updatedQueryString = stringify(parsed)
          let newUrl = window.location.pathname
          if (updatedQueryString) newUrl += `?${updatedQueryString}`
          window.history.pushState({}, '', newUrl)
          const sessionCount =
            Object.values(localCount).reduce((acc, val) => acc + val, 0) + 1
          Mixpanel.track(MIXPANEL_EVENTS.EXIT_INTENT_SUCCESS, {
            [MIXPANEL_PROPS.PROMOTION.name]:
              MIXPANEL_PROPS.PROMOTION.values.LIFETIME_5,
            [MIXPANEL_PROPS.SESSION_NUMBER.name]: sessionCount,
            [MIXPANEL_PROPS.USER_STATUS.name]:
              userStatus === 'unlogged' ? userStatus : 'free',
          })
          setTimeout(() => {
            updateUserInfo(() => {
              handleClose()
              seGetLifetimeOffer(false)
              history.push('/pricing')
            })
          }, 1500)
        })
        .catch(() => {
          seGetLifetimeOfferError(true)
          seGetLifetimeOffer(false)
          setOpen(true)
          if (isOpen && onClose) onClose()
        })
    },
    [handleClose, isOpen, localCount, onClose, updateUserInfo],
  )

  const handleCoupon = useCallback(
    (userStatus) => {
      if (!getUserTier()) return
      if (getUserTier() === 'anonymous') {
        setAppContext({
          ...contextValue,
          coupon: USER_COUPON,
        })
        handleClose()
        pushWithLanguageRoutes('/register')
      } else if (getUserTier() === 'free') setLifetimeOffer(userStatus)
    },
    [contextValue, handleClose, setAppContext, setLifetimeOffer],
  )

  useEffect(() => {
    if (isOpen && !getLifetimeOffer) handleCoupon('unlogged')
  }, [isOpen, getLifetimeOffer, handleCoupon])

  const Offers = [
    {
      icon: greyQuestionIcon,
      title: translate('discover_strategy'),
      content: translate('discover_strategy_subheading'),
    },
    {
      icon: greySearchIcon,
      title: translate('unlimited_content'),
      content: translate('unlimited_content_subheading'),
    },
    {
      icon: chartIcon,
      title: translate('monitor_ranking'),
      content: translate('monitor_ranking_subheading'),
    },
  ]

  return (
    <Modal
      isOpen={showModal}
      desktopStyle={{
        ...desktopStyle,
        background: isOpen ? 'transparent' : palette.white,
      }}
      mobileStyle={{
        background: isOpen ? 'transparent' : palette.white,
      }}
      overlayStyle={overlayStyle}
    >
      {isOpen ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <LoadingWheel width="50px" height="50px" color={palette.orange} />
        </div>
      ) : (
        <ModalContentWrapper>
          <ImageWrapper />
          <RightWrapper>
            <CloseButton onClick={hadleCount} styles={{ top: '20px' }} />
            <Text
              padding="8px 7px"
              fontSize="14px"
              fontFamily={fonts.primary}
              textAlign="center"
              color={palette.darkGray}
              style={{
                width: 'fit-content',
                background: palette.dullGreen,
                border: `1px solid ${palette.green}`,
                borderRadius: '2px',
              }}
            >
              {translate('one_time_offer').toUpperCase()}
            </Text>
            <Text
              margin="22px 0 0"
              fontSize="35px"
              lineHeight="1.2em"
              fontFamily={fonts.primaryMedium}
              textAlign="center"
              color={palette.darkGray}
              dangerouslySetInnerHTML={{
                __html: translate('unlock_ubs_heading'),
              }}
            />
            <Text
              margin="8px 0 0"
              fontSize="14px"
              lineHeight="1.5em"
              fontFamily={fonts.primary}
              textAlign="center"
              color={palette.darkGray}
              dangerouslySetInnerHTML={{
                __html: translate('unlock_ubs_subheading'),
              }}
            />
            <OffersWrapper>
              {Offers.map((offer, index) => (
                <OfferWrapper key={offer.title} order={index}>
                  <Icon src={offer.icon} size="42px" />
                  <Text
                    margin="17px 0 9px"
                    fontSize="14px"
                    lineHeight="1.25em"
                    fontFamily={fonts.primaryMedium}
                    textAlign="center"
                    color={palette.darkGray}
                  >
                    {offer.title}
                  </Text>
                  <Text
                    fontSize="12px"
                    lineHeight="1.5em"
                    fontFamily={fonts.primary}
                    textAlign="center"
                    color={palette.darkGray}
                  >
                    {offer.content}
                  </Text>
                </OfferWrapper>
              ))}
            </OffersWrapper>
            <ReviewWrapper>
              <Icon
                style={{ position: 'absolute', top: 24, left: 24 }}
                src={doubleQuotesIcon}
                width="42px"
                height="36px"
              />
              <AnnReviewWrapper>
                <Text
                  fontSize="16px"
                  lineHeight="1.5em"
                  fontFamily={fonts.primary}
                  color={palette.darkGray}
                >
                  {translate('ubs_review')}
                </Text>
                <AnnWrapper>
                  <Icon src={darellIcon} size="40px" />
                  <AnnInfoWrapper>
                    <Text
                      fontSize="14px"
                      lineHeight="1.5em"
                      fontFamily={fonts.primary}
                      color={palette.darkGray}
                    >
                      {translate('ann_handley')}
                    </Text>
                    <Text
                      fontSize="11px"
                      lineHeight="1.5em"
                      fontFamily={fonts.primary}
                      color={palette.darkGray}
                    >
                      {translate('marketing_expert')}
                    </Text>
                  </AnnInfoWrapper>
                </AnnWrapper>
              </AnnReviewWrapper>
              <ReviewUsersWrapper>
                <UsersWrapper>
                  <img
                    src={satisfiedUsersImage}
                    alt="Satisfied users"
                    width={102}
                    height={46}
                  />
                  <Text
                    margin="22px 0 0"
                    fontSize="21px"
                    lineHeight="1em"
                    fontFamily={fonts.primaryMedium}
                    color={palette.darkGray}
                  >
                    {translate('two_million')}
                  </Text>
                  <Text
                    margin="10px 0 0"
                    fontSize="18px"
                    lineHeight="1.5em"
                    fontFamily={fonts.primary}
                    color={palette.darkGray}
                  >
                    {translate('satisfied_users')}
                  </Text>
                </UsersWrapper>
              </ReviewUsersWrapper>
            </ReviewWrapper>
            <RightBottomWrapper>
              <ComparedMonthlySubscriptionWrapper>
                <Text
                  fontSize="21px"
                  lineHeight="1.5em"
                  fontFamily={fonts.primaryMedium}
                  color={palette.orange}
                >
                  {translate('save_ninety')}
                </Text>
                <Text
                  margin="0 0 0 12px"
                  fontSize="16px"
                  lineHeight="1em"
                  fontFamily={fonts.primary}
                  color={palette.primary}
                >
                  {translate('compares_monthly')}
                </Text>
              </ComparedMonthlySubscriptionWrapper>
              <OfferButton
                disabled={!!getLifetimeOffer}
                isLoading={!!getLifetimeOffer}
                theme="orange"
                tag={translate('get_lifetime_button')}
                onClick={handleCoupon}
              />
              <Text
                margin="6px 0 0"
                fontSize="12px"
                lineHeight="1.5em"
                fontFamily={fonts.primary}
                color={palette.darkGray}
              >
                {translate('offer_expires')}
              </Text>
              <LimitedOfferWrapper>
                <Icon className="rotating" src={starIcon} size={112} />
                <Text
                  fontSize="14px"
                  textAlign="center"
                  fontFamily={fonts.primaryMedium}
                  color={palette.white}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%) rotate(-15deg)',
                    wordBreak: 'normal',
                  }}
                >
                  {translate('limited_offer')}
                </Text>
              </LimitedOfferWrapper>
              <Text
                onClick={() => {
                  Mixpanel.track(MIXPANEL_EVENTS.INTENT_MODAL_EXIT, {
                    [MIXPANEL_PROPS.PROMOTION.name]:
                      MIXPANEL_PROPS.PROMOTION.values.LIFETIME_5,
                    [MIXPANEL_PROPS.DISMISSED_MODAL.name]:
                      MIXPANEL_PROPS.DISMISSED_MODAL.values.MODAL_REMIND_LATER,
                  })
                  handleClose()
                }}
                margin="6px 0 0"
                fontSize="12px"
                lineHeight="1.5em"
                fontFamily={fonts.primary}
                color={palette.blue}
                style={{
                  cursor: 'pointer',
                  position: 'absolute',
                  right: 0,
                  bottom: 0,
                  textDecoration: 'underline',
                }}
              >
                {translate('remind_later')}
              </Text>
            </RightBottomWrapper>
          </RightWrapper>
        </ModalContentWrapper>
      )}
    </Modal>
  )
}
